import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { api, Deposito, MotivoRechazo, fmtMonto, haceCuanto } from "../api/client";
import { Icon } from "../components/Icon";

/**
 * Cola de decisión: el analista responde "¿coincide o no?" y pasa al
 * siguiente sin volver a la lista.
 *
 * Deliberadamente NO se muestra nada técnico — execution id, rutas S3, modelo
 * de OCR, duración de Lambdas, la palabra "umbral". El analista decide sobre
 * el depósito, no sobre el sistema.
 */

/** Estados que devuelve el certifier, en palabras del analista. */
const ESTADO_ARCA: Record<string, string> = {
  CONFIRMADO: "Confirmado",
  PARCIAL: "Parcial",
  ESPERANDO_DATA: "ARCA no cargó aún",
  SIN_MOVIMIENTO_CON_COBERTURA: "Sin abono",
  BANCO_FUERA_DE_ARCA: "Banco fuera de ARCA",
  SIN_CODIGO: "Sin código de operación",
  SIN_FECHA: "Sin fecha",
  CONTRADICE: "Contradice el voucher",
  EXTORNADO: "Extornado",
  YA_CONSUMIDO: "Ya usado por otro depósito",
};

/**
 * Los motivos llegan planos del catálogo de Admin y se agrupan acá.
 *
 * Once opciones en una sola fila obligan a leerlas todas cada vez. Agrupadas,
 * el analista va directo al grupo que corresponde a lo que vio.
 *
 * El mapa va por `id` y no por etiqueta: si Admin renombra un motivo, la
 * agrupación sobrevive. Lo que no esté mapeado cae en "Otro", que es honesto
 * — mejor un grupo genérico que esconder una opción.
 */
const GRUPOS_MOTIVO: { titulo: string; ids: string[] }[] = [
  {
    titulo: "Un dato no cuadra",
    ids: ["698debf4dd04a76afe8e1d51", "698debf4dd04a76afe8e1d52",
          "698debf4dd04a76afe8e1d53", "698debf4dd04a76afe8e1d54",
          "698debf4dd04a76afe8e1d55", "698debf4dd04a76afe8e1d4d"],
  },
  {
    titulo: "Problema con el voucher",
    ids: ["698debf4dd04a76afe8e1d4e", "698debf4dd04a76afe8e1d4c"],
  },
  {
    titulo: "Problema con el depósito",
    ids: ["698debf4dd04a76afe8e1d4f", "69eacbb2166e01dadeb065b7",
          "69eacbb2166e01dadeb065b8"],
  },
];

/**
 * Título del banner rojo según la causa que manda el backend
 * (`veredicto.CAUSA_*`).
 *
 * Antes el banner decía «No coincide con el depósito esperado» en TODOS los
 * rojos, y en la mayoría eso contradecía a la tabla de abajo: monto ✓, banco ✓,
 * nº de operación ✓ y arriba un texto diciendo que no coincidía. Un voucher
 * reusado, un abono extornado o una operación fallida no son desacuerdos con
 * el Admin — son problemas distintos, y cada uno se resuelve mirando otra cosa.
 *
 * El subtítulo dice DÓNDE mirar, porque es lo que cambia entre causas: un
 * desacuerdo se resuelve en la imagen, un reuso en el otro depósito.
 */
const ROJO: Record<string, { titulo: string; sub: string }> = {
  CAMPO_NO_COINCIDE: {
    titulo: "No coincide con el depósito esperado",
    sub: "Revisa lo marcado antes de decidir.",
  },
  VOUCHER_REUSADO: {
    titulo: "Este voucher ya se usó en otro depósito",
    sub: "Los datos del comprobante cuadran. Lo que hay que revisar es el otro depósito, no la imagen.",
  },
  BENEFICIARIO_AJENO: {
    titulo: "El dinero no fue a Prestamype",
    sub: "El beneficiario del voucher es otro. Confírmalo en la imagen.",
  },
  OPERACION_FALLIDA: {
    titulo: "La operación del voucher no se completó",
    sub: "El propio comprobante dice que la transferencia falló.",
  },
  MOVIMIENTO_CONTRADICE: {
    titulo: "El abono del banco contradice al voucher",
    sub: "El voucher puede cuadrar con el Admin; lo que no cuadra es el movimiento que reporta el banco.",
  },
};

/** Sin causa —un pending anterior a que el backend la mandara— se cae al texto
 *  genérico, que no afirma de más. */
const ROJO_GENERICO = {
  titulo: "No aprobar",
  sub: "Revisa lo marcado antes de decidir.",
};

function rojo(causa?: string | null) {
  return (causa && ROJO[causa]) || ROJO_GENERICO;
}

type Ajuste = "ajustar" | "ancho" | "real";

export default function ReviewPage({
  cola, onResuelto,
}: {
  cola: Deposito[];
  onResuelto: () => void;
}) {
  const { id = "" } = useParams();
  const navigate = useNavigate();

  const [dep, setDep] = useState<Deposito | null>(null);
  const [motivos, setMotivos] = useState<MotivoRechazo[]>([]);
  const [motivo, setMotivo] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [zoom, setZoom] = useState(100);
  const [ajuste, setAjuste] = useState<Ajuste>("ajustar");
  const [giro, setGiro] = useState(0);
  const [voucher, setVoucher] = useState(0);
  const [filaActiva, setFilaActiva] = useState<string | null>(null);
  const [consultando, setConsultando] = useState(false);
  const [loteActivo, setLoteActivo] = useState(false);

  useEffect(() => {
    api.getConfig()
      .then((c: any) => setLoteActivo(c?.APROBACION_LOTE === true))
      .catch(() => setLoteActivo(false));
  }, []);

  /** Vuelve a preguntarle a ARCA ahora, sin esperar al barrido de 30 min. */
  async function volverAConsultar() {
    if (!dep || consultando) return;
    setConsultando(true); setError(null);
    try {
      const fresco = await api.recertificar(dep.id);
      setDep((viejo) => (viejo ? { ...viejo, ...fresco } : fresco));
      setToast(fresco.arca?.confirmado
        ? "✓ El banco confirmó el abono"
        : "ARCA sigue sin reportar este abono");
    } catch (e: any) {
      // «No pude preguntar» no es «no hay abono»: se dice con esas palabras.
      setError(e?.message ?? "No se pudo consultar ARCA ahora.");
    } finally {
      setConsultando(false);
    }
  }

  /** Los que el agente dio por concluyentes en verde, dentro de la cola con
   *  la que se está trabajando. Amarillo y rojo no entran: uno pide criterio
   *  y el otro pide rechazo, y ninguno se despacha sin abrirlo. */
  const yaCuadran = cola.filter(
    (d) => d.veredicto === "green" && d.arca?.confirmado !== false);

  async function aprobarLosQueCuadran() {
    if (!yaCuadran.length || enviando) return;
    setEnviando(true); setError(null);
    try {
      const r = await api.aprobarLote(yaCuadran.map((d) => d.id));
      setToast(r.fallidos.length
        ? `${r.aprobados.length} aprobados · ${r.fallidos.length} no se pudieron`
        : `✓ ${r.aprobados.length} aprobados`);
      onResuelto();
    } catch (e: any) {
      setError(e?.message ?? "No se pudo aprobar el lote.");
    } finally {
      setEnviando(false);
    }
  }

  const posicion = cola.findIndex((d) => d.id === id);
  const siguienteEnCola = posicion >= 0 ? cola[posicion + 1] : undefined;

  useEffect(() => {
    setDep(null); setMotivo(null); setError(null);
    setZoom(100); setVoucher(0); setGiro(0); setAjuste("ajustar");
    setFilaActiva(null);
    api.getPendingDetail(id).then(setDep).catch((e) => setError(e.message));
  }, [id]);

  useEffect(() => {
    api.getRejectReasons()
      .then((r) => setMotivos(r.items ?? []))
      .catch(() => setMotivos([]));
  }, []);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2600);
    return () => clearTimeout(t);
  }, [toast]);

  const decidir = useCallback(
    // `overrideArca` = aprobar sin que el banco haya confirmado el abono.
    // Viaja al backend y queda en el log: es el dato que hay que poder mirar
    // si más adelante aparece un abono que nunca entró.
    async (decision: "approved" | "rejected", overrideArca = false) => {
      if (!dep || enviando) return;
      if (decision === "rejected" && !motivo) {
        setError("Elige un motivo antes de rechazar.");
        return;
      }
      setEnviando(true); setError(null);
      try {
        const r = await api.decidir(dep.id, decision,
          decision === "rejected"
            ? { reject_reason: motivo! }
            : overrideArca ? { override_arca: true } : {});
        setToast(
          decision === "approved"
            ? `✓ Aprobado · ${dep.titular} · ${fmtMonto(dep.monto, dep.moneda)}`
            : `✕ Rechazado · ${dep.titular} · ${fmtMonto(dep.monto, dep.moneda)}`,
        );
        onResuelto();
        if (r.next_id) navigate(`/pendientes/${r.next_id}`);
        else navigate("/pendientes");
      } catch (e: any) {
        setError(e.message ?? "No se pudo registrar la decisión.");
      } finally {
        setEnviando(false);
      }
    },
    [dep, motivo, enviando, navigate, onResuelto],
  );

  const saltar = useCallback(() => {
    const siguiente = cola.find((d) => d.id !== id);
    setToast("Dejado para después");
    navigate(siguiente ? `/pendientes/${siguiente.id}` : "/pendientes");
  }, [cola, id, navigate]);

  const irA = useCallback((delta: number) => {
    const destino = cola[posicion + delta];
    if (destino) navigate(`/pendientes/${destino.id}`);
  }, [cola, posicion, navigate]);

  // Atajos: solo en esta pantalla y solo si no se está escribiendo.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const t = e.target as HTMLElement;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA")) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const k = e.key.toLowerCase();
      if (k === "a") { e.preventDefault(); decidir("approved"); }
      if (k === "r") { e.preventDefault(); decidir("rejected"); }
      if (k === "s") { e.preventDefault(); saltar(); }
      if (e.key === "ArrowLeft") { e.preventDefault(); irA(-1); }
      if (e.key === "ArrowRight") { e.preventDefault(); irA(1); }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [decidir, saltar, irA]);

  const grupos = useMemo(() => {
    const porId = new Map(motivos.map((m) => [m.id, m]));
    const usados = new Set<string>();
    const salida = GRUPOS_MOTIVO.map((g) => {
      const items = g.ids.map((i) => porId.get(i)).filter(Boolean) as MotivoRechazo[];
      items.forEach((m) => usados.add(m.id));
      return { titulo: g.titulo, items };
    }).filter((g) => g.items.length);
    const resto = motivos.filter((m) => !usados.has(m.id));
    if (resto.length) salida.push({ titulo: "Otro", items: resto });
    return salida;
  }, [motivos]);

  if (error && !dep) return <div className="empty">{error}</div>;
  if (!dep) return <div className="empty">Cargando el depósito...</div>;

  // El cartel sigue al VEREDICTO, no al conteo de diferencias. Atado a las
  // diferencias decía "todo coincide, puedes aprobar sin revisar" sobre
  // depósitos marcados en amarillo — el consejo opuesto al que corresponde.
  const tono = dep.veredicto === "green" ? "accent"
    : dep.veredicto === "yellow" ? "warn" : "danger";
  const coinciden = dep.comparacion.filter((f) => f.coincide && !f.sin_dato).length;
  const comparables = dep.comparacion.filter((f) => !f.sin_dato).length;

  const urls = dep.vouchers_url ?? [];
  const url = urls[voucher];
  const tipo = (url?.split("?")[0].split(".").pop() ?? "").toUpperCase().slice(0, 4);

  const estiloImagen: React.CSSProperties =
    ajuste === "ajustar" ? { maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }
    : ajuste === "ancho" ? { width: "100%", height: "auto" }
    : { width: `${zoom}%`, height: "auto" };

  return (
    <>
      {/* ── 2.1 Barra de la cola ─────────────────────────────────────── */}
      <div className="rev-barra">
        <button className="btn" onClick={() => navigate("/pendientes")}>
          <Icon name="back" /> Cola
        </button>

        <div className="rev-nav">
          <button className="icon-btn" disabled={posicion <= 0}
                  onClick={() => irA(-1)} aria-label="Anterior">‹</button>
          <span className="rev-pos tabular">
            {posicion >= 0 ? `${posicion + 1} de ${cola.length}` : `${cola.length} en cola`}
          </span>
          <button className="icon-btn" disabled={posicion < 0 || posicion >= cola.length - 1}
                  onClick={() => irA(1)} aria-label="Siguiente">›</button>
        </div>

        {cola.length > 0 && posicion >= 0 && (
          <div className="rev-avance" aria-hidden="true">
            <span style={{ width: `${100 * (posicion + 1) / cola.length}%` }} />
          </div>
        )}

        {siguienteEnCola && (
          <span className="rev-sigue">
            Sigue: <b>{siguienteEnCola.titular}</b>
          </span>
        )}

        <span style={{ flex: 1 }} />

        {loteActivo && yaCuadran.length > 0 && (
          <button className="btn rev-lote" disabled={enviando}
                  onClick={aprobarLosQueCuadran}>
            Aprobar {yaCuadran.length === 1
              ? "el que ya cuadra"
              : `los ${yaCuadran.length} que ya cuadran`}
          </button>
        )}

        <span className="rev-atajos mono">A aprobar · R rechazar · S después</span>
      </div>

      <div className="rev-grid">
        {/* ── 2.2 Visor ──────────────────────────────────────────────── */}
        <div className="card rev-visor">
          <div className="rev-toolbar">
            <span className="rev-toolbar-tit">Voucher enviado por el cliente</span>
            {tipo && <span className="rev-tipo mono">{tipo}</span>}

            {urls.length > 1 && (
              <span className="rev-pager tabular">
                <button className="icon-btn" disabled={voucher === 0}
                        onClick={() => setVoucher((v) => v - 1)}>‹</button>
                {voucher + 1}/{urls.length}
                <button className="icon-btn" disabled={voucher >= urls.length - 1}
                        onClick={() => setVoucher((v) => v + 1)}>›</button>
              </span>
            )}

            <span style={{ flex: 1 }} />

            <span className="rev-seg">
              {([["ajustar", "Ajustar"], ["ancho", "Ancho"], ["real", "1:1"]] as const)
                .map(([v, l]) => (
                  <button key={v} className={ajuste === v ? "on" : ""}
                          onClick={() => { setAjuste(v); if (v === "real") setZoom(100); }}>
                    {l}
                  </button>
                ))}
            </span>

            <button className="icon-btn" title="Rotar 90°"
                    onClick={() => setGiro((g) => (g + 90) % 360)}>⟳</button>

            {ajuste === "real" && (
              <span className="rev-zoom">
                <button className="icon-btn" onClick={() => setZoom((z) => Math.max(25, z - 25))}>−</button>
                <span className="tabular">{zoom}%</span>
                <button className="icon-btn" onClick={() => setZoom((z) => Math.min(400, z + 25))}>+</button>
              </span>
            )}

            {url && (
              <a className="btn" href={url} target="_blank" rel="noreferrer">Abrir ↗</a>
            )}
          </div>

          {/* A "Ajustar" la imagen entra COMPLETA: con alto fijo los vouchers
              verticales —la mayoría— quedaban cortados y había que scrollear
              dentro de una cajita para leer el monto. */}
          <div className="rev-lienzo-caja">
            {/* Rail de miniaturas. Aparece solo con varios comprobantes, que es
                el caso de los depósitos fraccionados: el cliente parte el pago
                porque su banca tiene tope por operación y sube un voucher por
                cada transferencia. Con ‹ › solos hay que pasar de a uno para
                encontrar el que falta; acá se ven todos a la vez. */}
            {urls.length > 1 && (
              <div className="rev-rail">
                {urls.map((u, n2) => (
                  <button key={u + n2}
                          className={"rev-mini" + (n2 === voucher ? " on" : "")}
                          onClick={() => setVoucher(n2)}
                          aria-label={`Comprobante ${n2 + 1} de ${urls.length}`}>
                    <img src={u} alt="" loading="lazy" />
                    <span className="rev-mini-n tabular">{n2 + 1}</span>
                  </button>
                ))}
              </div>
            )}

            <div className="rev-lienzo">
              {url ? (
                <img src={url} alt="Voucher enviado por el cliente"
                     style={{ ...estiloImagen, transform: `rotate(${giro}deg)`,
                              borderRadius: 8 }} />
              ) : (
                <div style={{ color: "var(--muted)" }}>No se pudo cargar la imagen</div>
              )}
            </div>
          </div>
        </div>

        {/* ── 2.3 Panel de decisión ──────────────────────────────────── */}
        <div className="rev-panel">
          {/* Identidad */}
          <div className="card card-pad">
            <div className="rev-eyebrow">Depósito esperado</div>
            <div className="rev-titular">{dep.titular ?? "—"}</div>
            <div className="rev-meta mono">
              {dep.origen} · op {dep.operacion ?? "—"}
              {dep.creado_en && <> · esperando {haceCuanto(dep.creado_en)}</>}
            </div>
            <div className="rev-monto tabular">{fmtMonto(dep.monto, dep.moneda)}</div>

            {/* Banner de veredicto */}
            <div className="rev-banner" style={{ background: `var(--${tono}-soft)` }}>
              <span className="rev-banner-icono"
                    style={{ background: `var(--${tono})` }}>
                {dep.veredicto === "green" ? "✓" : dep.veredicto === "yellow" ? "!" : "✕"}
              </span>
              <div>
                <div className="rev-banner-tit" style={{ color: `var(--${tono})` }}>
                  {dep.veredicto === "green" ? "Todo coincide con el depósito esperado"
                    : dep.veredicto === "yellow" ? "Necesita tu criterio"
                    : rojo(dep.veredicto_causa).titulo}
                </div>
                <div className="rev-banner-sub">
                  {dep.veredicto === "green"
                    ? `${coinciden} de ${comparables} datos verificados coinciden.`
                    : dep.veredicto === "yellow"
                      ? "El agente no pudo confirmarlo solo."
                      : rojo(dep.veredicto_causa).sub}
                </div>
                {dep.motivos?.length > 0 && (
                  <ul className="rev-motivos">
                    {dep.motivos.slice(0, 4).map((m) => <li key={m}>{m}</li>)}
                  </ul>
                )}
              </div>
            </div>
          </div>

          {/* Comparación — un valor por dato, no dos columnas */}
          <div className="card card-pad">
            <div className="rev-seccion">Voucher vs. depósito esperado</div>
            <div className="rev-datos">
              {dep.comparacion.map((f) => {
                const marca = f.sin_dato ? "?" : f.coincide ? "✓" : "✕";
                const color = f.sin_dato ? "var(--muted)"
                  : f.coincide ? "var(--accent)" : "var(--danger)";
                const activa = filaActiva === f.id;
                return (
                  <button key={f.id}
                          className={"rev-dato" + (activa ? " on" : "")}
                          onClick={() => setFilaActiva(activa ? null : f.id)}>
                    <span className="rev-marca" style={{ background: color }}>{marca}</span>
                    <span className="rev-dato-cuerpo">
                      <span className="rev-dato-label">{f.label}</span>
                      <span className="rev-dato-valor mono">
                        {valor(f.id, f.leido, dep.moneda)}
                      </span>
                      <span className="rev-dato-contraste">
                        {f.sin_dato ? "sin dato para comparar"
                          : f.coincide ? "igual a lo esperado"
                          : `esperado: ${valor(f.id, f.esperado, dep.moneda)}`}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* ARCA — tres estados, y en dos de ellos hay algo que hacer.
              Antes la tarjeta informaba y dejaba al analista sin salida:
              podía leer «el banco todavía no reporta este abono» y no tenía
              forma ni de volver a preguntar ni de seguir adelante. */}
          {(() => {
            const confirmado = dep.arca?.confirmado === true;
            const sinAbono = dep.arca?.estado === "SIN_MOVIMIENTO_CON_COBERTURA";
            const tonoArca = confirmado ? "ok" : sinAbono ? "mal" : "espera";
            return (
          <div className={"card card-pad rev-arca rev-arca-" + tonoArca}>
            <div className="rev-seccion-fila">
              <span className="rev-seccion">Movimiento bancario (ARCA)</span>
              <span className={"badge " + (confirmado ? "ok" : sinAbono ? "bad" : "warn")}>
                {ESTADO_ARCA[dep.arca?.estado ?? ""] ?? "No consultado"}
              </span>
            </div>
            <div className="rev-arca-texto">
              {confirmado
                ? "El banco confirmó el abono."
                : sinAbono
                  ? "El banco no registra ningún abono para este depósito."
                  : "El banco todavía no reporta este abono."}
            </div>

            {!confirmado && (
              <div className="rev-arca-acciones">
                <button className="btn" disabled={consultando || enviando}
                        onClick={volverAConsultar}>
                  {consultando ? "Consultando..." : "Volver a consultar ARCA"}
                </button>
                <button className="btn ghost" disabled={enviando || consultando}
                        onClick={() => decidir("approved", true)}>
                  Aprobar sin esperar a ARCA
                </button>
              </div>
            )}
            {!confirmado && (
              <div className="rev-arca-nota">
                Aprobar sin esperar queda registrado con tu nombre.
              </div>
            )}
            {!dep.arca?.visible && (
              <div className="rev-arca-nota">
                ARCA está en modo sombra: el resultado se calcula y se guarda,
                pero todavía no afecta el semáforo.
              </div>
            )}
            {dep.movimiento && (
              <div className="rev-arca-detalle">
                <FilaMov etiqueta="N° operación del banco"
                         valor={dep.movimiento.codigo_movimiento} destacar />
                <FilaMov etiqueta="Fecha del abono" valor={dep.movimiento.fecha} />
                <FilaMov etiqueta="Banco" valor={dep.movimiento.banco} />
                <FilaMov etiqueta="Concepto" valor={dep.movimiento.concepto} />
              </div>
            )}
          </div>
            );
          })()}

          {/* Motivos de rechazo, agrupados */}
          <div className="card card-pad">
            <div className="rev-seccion">Si rechazas, ¿por qué?</div>
            <div className="rev-seccion-sub">un clic — se envía con el rechazo</div>
            {grupos.length === 0 && (
              <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 8 }}>
                No se pudo cargar el catálogo de motivos.
              </div>
            )}
            {grupos.map((g) => (
              <div key={g.titulo} className="rev-grupo">
                <div className="rev-grupo-tit">{g.titulo}</div>
                <div className="rev-chips">
                  {g.items.map((m) => (
                    <button key={m.id}
                            className={"rev-chip" + (motivo === m.id ? " on" : "")}
                            onClick={() => setMotivo(motivo === m.id ? null : m.id)}>
                      {m.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {error && <div className="rev-error">{error}</div>}

          {/* Barra de acción */}
          <div className="rev-acciones">
            <button className="btn primary rev-aprobar" disabled={enviando}
                    onClick={() => decidir("approved")}>
              Aprobar depósito <kbd>A</kbd>
            </button>
            <button className="btn danger" disabled={enviando || !motivo}
                    title={motivo ? undefined : "Elige un motivo primero"}
                    onClick={() => decidir("rejected")}>
              Rechazar <kbd>R</kbd>
            </button>
            <button className="btn ghost" disabled={enviando} onClick={saltar}>
              Después <kbd>S</kbd>
            </button>
          </div>
          <div className="rev-nota-envio">
            {motivo
              ? "Al rechazar se envía el motivo elegido al Admin."
              : "Al decidir pasas al siguiente automáticamente."}
          </div>
        </div>
      </div>

      {toast && <div className="toast">{toast}</div>}
    </>
  );
}

/** Una fila del bloque de ARCA. Lo que no vino se dice, no se deja en blanco:
 *  un espacio vacío se lee como «no aplica» y acá casi siempre significa
 *  «todavía no llegó». */
function FilaMov({ etiqueta, valor, destacar }: {
  etiqueta: string;
  valor: string | null;
  destacar?: boolean;
}) {
  return (
    <div className="rev-mov-fila">
      <div className="rev-mov-label">{etiqueta}</div>
      <div className="mono"
           style={{ fontSize: destacar ? 13 : 12.5,
                    fontWeight: destacar ? 600 : 400,
                    color: valor ? "var(--text)" : "var(--faint)" }}>
        {valor || "sin dato"}
      </div>
    </div>
  );
}

function valor(id: string, v: unknown, moneda?: string | null) {
  if (v === null || v === undefined || v === "") return "—";
  if (id === "monto") return fmtMonto(Number(v), moneda);
  if (id === "fecha") {
    // El esperado viene como epoch; el leído del voucher como ISO.
    const d = typeof v === "number" ? new Date(v * 1000) : new Date(String(v));
    return isNaN(d.getTime()) ? String(v) : d.toLocaleString("es-PE", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  }
  return String(v);
}
